import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminService } from '@/api/services/admin.service';
import { queryKeys } from '../query-keys';
import { useToast } from '@/hooks/use-toast';
import { useLanguage } from '@/contexts/LanguageContext';
import { getErrorMessage } from '@/api/errors/error-handler';
import type {
  CreateHomepageFeatureRequest,
  UpdateHomepageFeatureRequest,
  CreateHomepageStatisticRequest,
  UpdateHomepageStatisticRequest,
  CreateHomepageContentRequest,
  CreateAboutValueRequest,
  UpdateAboutValueRequest,
  CreateAboutContentRequest,
  CreateHowItWorksStepRequest,
  UpdateHowItWorksStepRequest,
  CreateHowItWorksContentRequest,
  CreateFAQRequest,
  UpdateFAQRequest,
  CreateContactContentRequest,
  CreateNavigationLinkRequest,
  UpdateNavigationLinkRequest,
  CreateFooterContentRequest,
} from '@/api/types';

// Homepage Content
export const useHomepageContent = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: queryKeys.adminContent.homepage(),
    queryFn: () => adminService.getHomepageContent(),
    enabled: options?.enabled !== false,
  });
};

export const useCreateOrUpdateHomepageContent = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: (data: CreateHomepageContentRequest) =>
      adminService.createOrUpdateHomepageContent(data),
    onSuccess: () => {
      // Invalidate both admin and public queries to ensure UI updates
      queryClient.invalidateQueries({ queryKey: queryKeys.adminContent.homepage() });
      queryClient.invalidateQueries({ queryKey: queryKeys.public.content.homepage() });
      toast({
        title: t('content.homepage.updated') || 'تم تحديث محتوى الصفحة الرئيسية',
        description: t('content.homepage.updatedDesc') || 'تم تحديث محتوى الصفحة الرئيسية بنجاح',
      });
    },
    onError: (error) => {
      toast({
        title: t('common.error') || 'خطأ',
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    },
  });
};

// Homepage Features
export const useHomepageFeatures = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: queryKeys.adminContent.homepageFeatures(),
    queryFn: () => adminService.getHomepageFeatures(),
    enabled: options?.enabled !== false,
  });
};

export const useCreateHomepageFeature = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: (data: CreateHomepageFeatureRequest) =>
      adminService.createHomepageFeature(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adminContent.homepageFeatures() });
      queryClient.invalidateQueries({ queryKey: queryKeys.adminContent.homepage() });
      queryClient.invalidateQueries({ queryKey: queryKeys.public.content.homepage() });
      toast({
        title: t('content.feature.created') || 'تم إنشاء الميزة',
        description: t('content.feature.createdDesc') || 'تم إنشاء الميزة بنجاح',
      });
    },
    onError: (error) => {
      toast({
        title: t('common.error') || 'خطأ',
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    },
  });
};

export const useUpdateHomepageFeature = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateHomepageFeatureRequest }) =>
      adminService.updateHomepageFeature(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adminContent.homepageFeatures() });
      queryClient.invalidateQueries({ queryKey: queryKeys.adminContent.homepage() });
      queryClient.invalidateQueries({ queryKey: queryKeys.public.content.homepage() });
      toast({
        title: t('content.feature.updated') || 'تم تحديث الميزة',
        description: t('content.feature.updatedDesc') || 'تم تحديث الميزة بنجاح',
      });
    },
    onError: (error) => {
      toast({
        title: t('common.error') || 'خطأ',
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    },
  });
};

export const useDeleteHomepageFeature = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: (id: number) => adminService.deleteHomepageFeature(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adminContent.homepageFeatures() });
      queryClient.invalidateQueries({ queryKey: queryKeys.adminContent.homepage() });
      queryClient.invalidateQueries({ queryKey: queryKeys.public.content.homepage() });
      toast({
        title: t('content.feature.deleted') || 'تم حذف الميزة',
        description: t('content.feature.deletedDesc') || 'تم حذف الميزة بنجاح',
      });
    },
    onError: (error) => {
      toast({
        title: t('common.error') || 'خطأ',
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    },
  });
};

// Homepage Statistics
export const useHomepageStatistics = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: queryKeys.adminContent.homepageStatistics(),
    queryFn: () => adminService.getHomepageStatistics(),
    enabled: options?.enabled !== false,
  });
};

export const useCreateHomepageStatistic = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: (data: CreateHomepageStatisticRequest) =>
      adminService.createHomepageStatistic(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adminContent.homepageStatistics() });
      queryClient.invalidateQueries({ queryKey: queryKeys.adminContent.homepage() });
      queryClient.invalidateQueries({ queryKey: queryKeys.public.content.homepage() });
      toast({
        title: t('content.statistic.created') || 'تم إنشاء الإحصائية',
        description: t('content.statistic.createdDesc') || 'تم إنشاء الإحصائية بنجاح',
      });
    },
    onError: (error) => {
      toast({
        title: t('common.error') || 'خطأ',
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    },
  });
};

export const useUpdateHomepageStatistic = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateHomepageStatisticRequest }) =>
      adminService.updateHomepageStatistic(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adminContent.homepageStatistics() });
      queryClient.invalidateQueries({ queryKey: queryKeys.adminContent.homepage() });
      queryClient.invalidateQueries({ queryKey: queryKeys.public.content.homepage() });
      toast({
        title: t('content.statistic.updated') || 'تم تحديث الإحصائية',
        description: t('content.statistic.updatedDesc') || 'تم تحديث الإحصائية بنجاح',
      });
    },
    onError: (error) => {
      toast({
        title: t('common.error') || 'خطأ',
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    },
  });
};

export const useDeleteHomepageStatistic = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: (id: number) => adminService.deleteHomepageStatistic(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adminContent.homepageStatistics() });
      queryClient.invalidateQueries({ queryKey: queryKeys.adminContent.homepage() });
      queryClient.invalidateQueries({ queryKey: queryKeys.public.content.homepage() });
      toast({
        title: t('content.statistic.deleted') || 'تم حذف الإحصائية',
        description: t('content.statistic.deletedDesc') || 'تم حذف الإحصائية بنجاح',
      });
    },
    onError: (error) => {
      toast({
        title: t('common.error') || 'خطأ',
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    },
  });
};

// About Content
export const useAboutContent = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: queryKeys.adminContent.about(),
    queryFn: () => adminService.getAboutContent(),
    enabled: options?.enabled !== false,
  });
};

export const useCreateOrUpdateAboutContent = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: (data: CreateAboutContentRequest) =>
      adminService.createOrUpdateAboutContent(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adminContent.about() });
      queryClient.invalidateQueries({ queryKey: queryKeys.public.content.about() });
      toast({
        title: t('content.about.updated') || 'تم تحديث محتوى صفحة من نحن',
        description: t('content.about.updatedDesc') || 'تم تحديث محتوى صفحة من نحن بنجاح',
      });
    },
    onError: (error) => {
      toast({
        title: t('common.error') || 'خطأ',
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    },
  });
};

// About Values
export const useAboutValues = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: queryKeys.adminContent.aboutValues(),
    queryFn: () => adminService.getAboutValues(),
    enabled: options?.enabled !== false,
  });
};

export const useCreateAboutValue = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: (data: CreateAboutValueRequest) => adminService.createAboutValue(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adminContent.aboutValues() });
      queryClient.invalidateQueries({ queryKey: queryKeys.adminContent.about() });
      queryClient.invalidateQueries({ queryKey: queryKeys.public.content.about() });
      toast({
        title: t('content.value.created') || 'تم إنشاء القيمة',
        description: t('content.value.createdDesc') || 'تم إنشاء القيمة بنجاح',
      });
    },
    onError: (error) => {
      toast({
        title: t('common.error') || 'خطأ',
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    },
  });
};

export const useUpdateAboutValue = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateAboutValueRequest }) =>
      adminService.updateAboutValue(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adminContent.aboutValues() });
      queryClient.invalidateQueries({ queryKey: queryKeys.adminContent.about() });
      queryClient.invalidateQueries({ queryKey: queryKeys.public.content.about() });
      toast({
        title: t('content.value.updated') || 'تم تحديث القيمة',
        description: t('content.value.updatedDesc') || 'تم تحديث القيمة بنجاح',
      });
    },
    onError: (error) => {
      toast({
        title: t('common.error') || 'خطأ',
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    },
  });
};

export const useDeleteAboutValue = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: (id: number) => adminService.deleteAboutValue(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adminContent.aboutValues() });
      queryClient.invalidateQueries({ queryKey: queryKeys.adminContent.about() });
      queryClient.invalidateQueries({ queryKey: queryKeys.public.content.about() });
      toast({
        title: t('content.value.deleted') || 'تم حذف القيمة',
        description: t('content.value.deletedDesc') || 'تم حذف القيمة بنجاح',
      });
    },
    onError: (error) => {
      toast({
        title: t('common.error') || 'خطأ',
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    },
  });
};

// How It Works Content
export const useHowItWorksContent = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: queryKeys.adminContent.howItWorks(),
    queryFn: () => adminService.getHowItWorksContent(),
    enabled: options?.enabled !== false,
  });
};

export const useCreateOrUpdateHowItWorksContent = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: (data: CreateHowItWorksContentRequest) =>
      adminService.createOrUpdateHowItWorksContent(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adminContent.howItWorks() });
      queryClient.invalidateQueries({ queryKey: queryKeys.public.content.howItWorks() });
      toast({
        title: t('content.howItWorks.updated') || 'تم تحديث محتوى صفحة كيف يعمل',
        description: t('content.howItWorks.updatedDesc') || 'تم تحديث محتوى صفحة كيف يعمل بنجاح',
      });
    },
    onError: (error) => {
      toast({
        title: t('common.error') || 'خطأ',
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    },
  });
};

// How It Works Steps
export const useHowItWorksSteps = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: queryKeys.adminContent.howItWorksSteps(),
    queryFn: () => adminService.getHowItWorksSteps(),
    enabled: options?.enabled !== false,
  });
};

export const useCreateHowItWorksStep = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: (data: CreateHowItWorksStepRequest) =>
      adminService.createHowItWorksStep(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adminContent.howItWorksSteps() });
      queryClient.invalidateQueries({ queryKey: queryKeys.adminContent.howItWorks() });
      queryClient.invalidateQueries({ queryKey: queryKeys.public.content.howItWorks() });
      toast({
        title: t('content.step.created') || 'تم إنشاء الخطوة',
        description: t('content.step.createdDesc') || 'تم إنشاء الخطوة بنجاح',
      });
    },
    onError: (error) => {
      toast({
        title: t('common.error') || 'خطأ',
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    },
  });
};

export const useUpdateHowItWorksStep = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateHowItWorksStepRequest }) =>
      adminService.updateHowItWorksStep(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adminContent.howItWorksSteps() });
      queryClient.invalidateQueries({ queryKey: queryKeys.adminContent.howItWorks() });
      queryClient.invalidateQueries({ queryKey: queryKeys.public.content.howItWorks() });
      toast({
        title: t('content.step.updated') || 'تم تحديث الخطوة',
        description: t('content.step.updatedDesc') || 'تم تحديث الخطوة بنجاح',
      });
    },
    onError: (error) => {
      toast({
        title: t('common.error') || 'خطأ',
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    },
  });
};

export const useDeleteHowItWorksStep = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: (id: number) => adminService.deleteHowItWorksStep(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adminContent.howItWorksSteps() });
      queryClient.invalidateQueries({ queryKey: queryKeys.adminContent.howItWorks() });
      queryClient.invalidateQueries({ queryKey: queryKeys.public.content.howItWorks() });
      toast({
        title: t('content.step.deleted') || 'تم حذف الخطوة',
        description: t('content.step.deletedDesc') || 'تم حذف الخطوة بنجاح',
      });
    },
    onError: (error) => {
      toast({
        title: t('common.error') || 'خطأ',
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    },
  });
};

// FAQs
export const useFAQs = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: queryKeys.adminContent.faqs(),
    queryFn: () => adminService.getFAQs(),
    enabled: options?.enabled !== false,
  });
};

export const useCreateFAQ = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: (data: CreateFAQRequest) => adminService.createFAQ(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adminContent.faqs() });
      queryClient.invalidateQueries({ queryKey: queryKeys.public.content.faqs() });
      toast({
        title: t('content.faq.created') || 'تم إنشاء السؤال',
        description: t('content.faq.createdDesc') || 'تم إنشاء السؤال بنجاح',
      });
    },
    onError: (error) => {
      toast({
        title: t('common.error') || 'خطأ',
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    },
  });
};

export const useUpdateFAQ = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateFAQRequest }) =>
      adminService.updateFAQ(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adminContent.faqs() });
      queryClient.invalidateQueries({ queryKey: queryKeys.public.content.faqs() });
      toast({
        title: t('content.faq.updated') || 'تم تحديث السؤال',
        description: t('content.faq.updatedDesc') || 'تم تحديث السؤال بنجاح',
      });
    },
    onError: (error) => {
      toast({
        title: t('common.error') || 'خطأ',
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    },
  });
};

export const useDeleteFAQ = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: (id: number) => adminService.deleteFAQ(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adminContent.faqs() });
      queryClient.invalidateQueries({ queryKey: queryKeys.public.content.faqs() });
      toast({
        title: t('content.faq.deleted') || 'تم حذف السؤال',
        description: t('content.faq.deletedDesc') || 'تم حذف السؤال بنجاح',
      });
    },
    onError: (error) => {
      toast({
        title: t('common.error') || 'خطأ',
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    },
  });
};

// Contact Content
export const useContactContent = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: queryKeys.adminContent.contact(),
    queryFn: () => adminService.getContactContent(),
    enabled: options?.enabled !== false,
  });
};

export const useCreateOrUpdateContactContent = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: (data: CreateContactContentRequest) =>
      adminService.createOrUpdateContactContent(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adminContent.contact() });
      queryClient.invalidateQueries({ queryKey: queryKeys.public.content.contact() });
      toast({
        title: t('content.contact.updated') || 'تم تحديث محتوى صفحة التواصل',
        description: t('content.contact.updatedDesc') || 'تم تحديث محتوى صفحة التواصل بنجاح',
      });
    },
    onError: (error) => {
      toast({
        title: t('common.error') || 'خطأ',
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    },
  });
};

// Navigation Links
export const useNavigationLinks = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: queryKeys.adminContent.navigation(),
    queryFn: () => adminService.getNavigationLinks(),
    enabled: options?.enabled !== false,
  });
};

export const useCreateNavigationLink = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: (data: CreateNavigationLinkRequest) =>
      adminService.createNavigationLink(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adminContent.navigation() });
      queryClient.invalidateQueries({ queryKey: queryKeys.public.content.navigation() });
      toast({
        title: t('content.navigation.created') || 'تم إنشاء رابط',
        description: t('content.navigation.createdDesc') || 'تم إنشاء رابط بنجاح',
      });
    },
    onError: (error) => {
      toast({
        title: t('common.error') || 'خطأ',
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    },
  });
};

export const useUpdateNavigationLink = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateNavigationLinkRequest }) =>
      adminService.updateNavigationLink(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adminContent.navigation() });
      queryClient.invalidateQueries({ queryKey: queryKeys.public.content.navigation() });
      toast({
        title: t('content.navigation.updated') || 'تم تحديث الرابط',
        description: t('content.navigation.updatedDesc') || 'تم تحديث الرابط بنجاح',
      });
    },
    onError: (error) => {
      toast({
        title: t('common.error') || 'خطأ',
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    },
  });
};

export const useDeleteNavigationLink = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: (id: number) => adminService.deleteNavigationLink(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adminContent.navigation() });
      queryClient.invalidateQueries({ queryKey: queryKeys.public.content.navigation() });
      toast({
        title: t('content.navigation.deleted') || 'تم حذف الرابط',
        description: t('content.navigation.deletedDesc') || 'تم حذف الرابط بنجاح',
      });
    },
    onError: (error) => {
      toast({
        title: t('common.error') || 'خطأ',
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    },
  });
};

// Footer Content
export const useFooterContent = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: queryKeys.adminContent.footer(),
    queryFn: () => adminService.getFooterContent(),
    enabled: options?.enabled !== false,
  });
};

export const useCreateOrUpdateFooterContent = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useLanguage();

  return useMutation({
    mutationFn: (data: CreateFooterContentRequest) =>
      adminService.createOrUpdateFooterContent(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.adminContent.footer() });
      queryClient.invalidateQueries({ queryKey: queryKeys.public.content.footer() });
      toast({
        title: t('content.footer.updated') || 'تم تحديث محتوى التذييل',
        description: t('content.footer.updatedDesc') || 'تم تحديث محتوى التذييل بنجاح',
      });
    },
    onError: (error) => {
      toast({
        title: t('common.error') || 'خطأ',
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    },
  });
};
